import { useState, ReactNode, useCallback } from 'react';
import { LoadingContext } from './LoadingContext';
import type { LoadingContextType } from './types';

interface LoadingProviderProps {
  children: ReactNode;
}

export function LoadingProvider({ children }: LoadingProviderProps) {
  const [isLoading, setIsLoading] = useState(false);

  const startLoading = useCallback(() => {
    setIsLoading(true);
    // Ensure the loading spinner is visible
    const loadingElement = document.querySelector('.app-loading');
    if (loadingElement) {
      // Remove any previous animation classes
      loadingElement.classList.remove('fade-out');
      loadingElement.classList.remove('hidden');
      // Force reflow to ensure classes are applied
      void (loadingElement as HTMLElement).offsetWidth;
    }
  }, []);

  const stopLoading = useCallback(() => {
    setIsLoading(false);
    // Fade out the loading spinner
    const loadingElement = document.querySelector('.app-loading');
    if (loadingElement) {
      // Remove fade-out class if it was added before
      loadingElement.classList.remove('fade-out');
      // Force reflow to restart animation
      void (loadingElement as HTMLElement).offsetWidth;
      // Add fade-out class to trigger animation
      loadingElement.classList.add('fade-out');
      // Hide the element after animation completes
      setTimeout(() => {
        loadingElement.classList.add('hidden');
      }, 300);
    }
  }, []);

  const withLoading = useCallback(async <T,>(promise: Promise<T>): Promise<T> => {
    try {
      startLoading();
      return await promise;
    } finally {
      stopLoading();
    }
  }, [startLoading, stopLoading]);

  const value: LoadingContextType = {
    isLoading,
    startLoading,
    stopLoading,
    withLoading,
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
    </LoadingContext.Provider>
  );
}
